import {useCallback, useMemo, useState} from 'react';
import assert from 'assert-ts';
import type {Valid} from 'schemaDefinition/types';
import type {
  ManifestationValidationResultV0,
  MetadataValidationResult,
} from 'schemas/types';
import type {ExpressionV4, ManifestationV4} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {aggregateValid} from 'schemaDefinition/functions';
import type {CodelistRestrictor} from 'services/codelists';
import {
  assertExpression,
  assertManifestation,
  useMetadataSchemas,
  useMetadataSchemasCodelists,
} from 'services/metadata';
import {assertEntityStatus} from 'services/metadata/functions';
import {validateNewExpressionWithManifestation} from 'services/metadata/functions/validateNewExpressionWithManifestation';
import {useAppStore} from 'store/hooks';
import {useSnacks} from 'components';
import {trimDataValue} from 'schema/form/functions';
import {useExpressionAndManifestationCodelistRestrictor} from 'schemas/codelistRestrictors/hooks/useExpressionAndManifestationCodelistRestrictor';
import {useMetadataOperationsContext} from 'scenes/updateMetadata/contexts';
import type {ExpressionCardCoreProps} from '../../ExpressionCard/types';
import type {ManifestationCardProps} from '../../ManifestationCard/types';
import {useExpressionEditStateCore} from '../../ExpressionCard/hooks/useExpressionEditStateCore';
import {useManifestationEditState} from '../../ManifestationCard/hooks';

export const useNewExpressionWithManifestationEditState = (
  workId: string,
  expression: ExpressionV4,
  manifestationId: string,
): {
  expressionEditState: ReturnType<typeof useExpressionEditStateCore>;
  manifestationEditState: ReturnType<typeof useManifestationEditState>;
  codelistRestrictor: CodelistRestrictor;
  valid: Valid | undefined;
  showErrors: boolean;
  saving: boolean;
  handleSave: (
    saveWithWarnings?: boolean,
  ) => Promise<ManifestationValidationResultV0>;
  handleCancel: () => void;
} => {
  const {getState} = useAppStore();
  const {t, tryT} = useLocalization();

  const metadataSchemas = useMetadataSchemas();
  const metadataCodelists = useMetadataSchemasCodelists();

  const expressionParams = useMemo(
    (): ExpressionCardCoreProps => ({
      workId,
      expressionId: expression.id,
      stableExpression: expression,
      readonly: false,
    }),
    [expression, workId],
  );

  const expressionEditState = useExpressionEditStateCore(expressionParams);

  const manifestationParams = useMemo(
    (): ManifestationCardProps => ({
      workId,
      expressionId: expression.id,
      manifestationId,
      // TODO: Also move change requests
      changeRequests: undefined,
      readonly: false,
      initialExpanded: true,
    }),
    [expression, manifestationId, workId],
  );

  const manifestationEditState = useManifestationEditState(manifestationParams);

  const [valid, setValid] = useState<Valid | undefined>(undefined);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  const {successSnack, errorSnack} = useSnacks();

  const {
    saveNewExpressionWithManifestation,
    cancelMoveManifestationToNewExpression,
  } = useMetadataOperationsContext();

  const codelistRestrictor = useExpressionAndManifestationCodelistRestrictor(
    manifestationId,
    expression.id,
  );

  const handleValidate = useCallback((): MetadataValidationResult => {
    const {savedMetadata, metadata, statuses} = getState().metadataEdit;
    const validation = validateNewExpressionWithManifestation({
      savedWork: assert(
        savedMetadata?.work,
        'handleValidate: savedWork expected',
      ),
      work: assert(metadata?.work, 'handleValidate: work expected'),
      expression: assert(
        metadata?.expressions.find(e => e.id === expression.id),
        'handleValidate: expression expected',
      ),
      savedManifestation: assert(
        savedMetadata?.manifestations.find(m => m.id === manifestationId),
        'handleValidate: savedManifestation expected',
      ),
      manifestation: assert(
        metadata?.manifestations.find(m => m.id === manifestationId),
        'handleValidate: manifestation expected',
      ),
      configuration: {
        schemas: metadataSchemas,
        codelists: metadataCodelists,
      },
      statuses,
    });

    return validation;
  }, [
    expression.id,
    getState,
    manifestationId,
    metadataCodelists,
    metadataSchemas,
  ]);

  const handleSave = useCallback(
    (saveWithWarnings?: boolean): Promise<ManifestationValidationResultV0> => {
      const currentExpressoinEdit = assertExpression(
        expression.id,
        getState().metadataEdit.metadata,
      );

      const currentManifestationEdit = assertManifestation(
        manifestationId,
        getState().metadataEdit.metadata,
      );

      const manifestationStatus = assertEntityStatus(
        manifestationId,
        getState().metadataEdit,
      );

      const trimmedExpression: ExpressionV4 = trimDataValue(
        currentExpressoinEdit,
        expressionEditState.schema,
      );

      const trimmedManifestationEditWithStatus: ManifestationV4 = {
        ...trimDataValue(
          currentManifestationEdit,
          manifestationEditState.schema,
        ),
        status: manifestationStatus.changedStatus ?? manifestationStatus.status,
      };

      const validation = handleValidate();

      if (
        validation.valid === 'error' ||
        (!saveWithWarnings && validation.valid === 'warning')
      ) {
        setShowErrors(true);
        return Promise.resolve(validation);
      }

      setShowErrors(false);
      setValid(undefined);
      setSaving(true);

      return saveNewExpressionWithManifestation(
        trimmedExpression,
        trimmedManifestationEditWithStatus,
      ).then(saveStatus => {
        if (saveStatus.status === 'Saved') {
          successSnack(t('page.metadata.newExpression.save.success'));
        } else {
          const messageKey = saveStatus.error?.message;

          const message =
            tryT(`error.api.${messageKey}`) ??
            t('page.metadata.newExpression.save.failed');

          errorSnack(message, saveStatus.error);
        }
        setSaving(false);
        return validation;
      });
    },
    [
      expression,
      getState,
      manifestationId,
      expressionEditState.schema,
      manifestationEditState.schema,
      handleValidate,
      saveNewExpressionWithManifestation,
      successSnack,
      t,
      tryT,
      errorSnack,
    ],
  );

  const handleCancel = useCallback(
    () => cancelMoveManifestationToNewExpression(manifestationId),
    [cancelMoveManifestationToNewExpression, manifestationId],
  );

  return useMemo(() => {
    const aggregatedValid = aggregateValid(
      valid,
      expressionEditState.valid,
      manifestationEditState.valid,
    );
    return {
      expressionEditState,
      manifestationEditState,
      codelistRestrictor,
      valid: aggregatedValid,
      showErrors: showErrors || expressionEditState.showErrors,
      saving,
      handleSave,
      handleCancel,
    };
  }, [
    codelistRestrictor,
    expressionEditState,
    handleCancel,
    handleSave,
    manifestationEditState,
    saving,
    showErrors,
    valid,
  ]);
};
